// Gate check-in PWA (spec §8): fully validates and records check-ins with ZERO
// network connectivity. Deliberately framework-light (§10) — no SSR/hydration
// interacting with service-worker caching and IndexedDB.

// Design system: same tokens and classes as the web app — fonts are self-hosted
// so the PWA styles fully offline once precached.
import '@kontriticket/design/fonts.css';
import '@kontriticket/design/tokens.css';
import '@kontriticket/design/components.css';
import './styles.css';

import { getMeta, getTicket, getTicketByNumber, markCheckedIn, queueScan, countOutbox } from './db.js';
import { checkInOnline } from './api.js';
import { VERDICT, verifyTicketQr } from './verify.js';
import { Scanner } from './scanner.js';
import { readiness, syncNow, registerBackgroundSync } from './sync.js';
import { clearTokenFromLocation, runProvisioning, tokenFromLocation, tokenFromText } from './provision.js';
import { centre, el, render, statusBar, verdictOverlay } from './ui.js';

const VERDICT_MS = 2200;

const state = {
  meta: {},
  queued: 0,
  scanner: null,
  showingVerdict: false,
};

// --------------------------------------------------------------- the decision

/**
 * The §8.3 scan flow. THE ORDER IS THE REQUIREMENT, not an implementation
 * detail:
 *
 *   signature -> event -> local status -> already used -> absence
 *
 * Absence must be the LAST question asked. If it were asked earlier, a
 * cancelled ticket — which is present in the dataset carrying
 * `status: 'cancelled'` precisely so this check can catch it — would instead
 * be treated as "genuine, sold after your last sync" and ADMITTED. A refunded
 * delegate would be waved through by the rule written to protect legitimate
 * late buyers (research R13).
 */
async function decideFromQr(qr) {
  const publicKey = state.meta.publicKey;
  const verified = await verifyTicketQr(qr, publicKey);
  if (!verified.ok) return { verdict: verified.reason };

  const { payload } = verified;
  if (payload.eventId !== state.meta.eventId) {
    return { verdict: VERDICT.WRONG_EVENT };
  }

  const ticket = await getTicket(payload.registrationId);
  return decideFromTicket(ticket, payload.registrationId);
}

/**
 * Shared tail of the decision. Manual entry joins here — steps 1–3 have no
 * meaning for a typed number, which is exactly why a typed number can never be
 * forged into an admission: it has to be in the downloaded set already.
 * ADMIT_UNKNOWN is therefore unreachable by hand, and that asymmetry is
 * correct.
 */
async function decideFromTicket(ticket, registrationId) {
  if (!ticket) {
    // Verified signature, this event, absent from the list: sold after this
    // device last synced. Admit — the signature already proves it is genuine,
    // and refusing would punish a legitimate buyer for a sync gap (§8.3).
    return { verdict: VERDICT.ADMIT_UNKNOWN, registrationId };
  }
  if (ticket.status === 'cancelled') {
    return { verdict: VERDICT.REJECT, ticket, note: 'This ticket was cancelled.' };
  }
  if (ticket.checkedInLocally || ticket.status === 'checked_in') {
    return { verdict: VERDICT.DUPLICATE, ticket };
  }
  return { verdict: VERDICT.ADMIT, ticket };
}

const ADMITTING = new Set([VERDICT.ADMIT, VERDICT.ADMIT_UNKNOWN]);

/**
 * Verdict -> the `scan_events.outcome` literal that records it.
 *
 * The vocabulary is exactly `first_checkin | duplicate_rejected` (008
 * data-model; enforced by the CHECK constraint in migration 0018). A rejection
 * has no literal of its own, so it is NOT queued: labelling a cancelled
 * ticket's refusal `first_checkin` would tell feature 009 that a refunded
 * delegate walked in, and the reconciliation feed would show an admission that
 * never happened. Absent is wrong; a lie about the money path is worse.
 */
const OUTCOME = new Map([
  [VERDICT.ADMIT, 'first_checkin'],
  [VERDICT.ADMIT_UNKNOWN, 'first_checkin'],
  [VERDICT.DUPLICATE, 'duplicate_rejected'],
]);

/**
 * ⚠️  THE ONLINE ATTEMPT WRAPS AROUND THE LOCAL DECISION. IT DOES NOT REPLACE IT.
 *
 * `decideFromQr` / `decideFromTicket` are UNTOUCHED by feature 009 (research R7,
 * FR-015..017). This asks the server first when there is any point in asking,
 * and on ANY non-answer falls through to exactly the verdict this device would
 * have reached alone. A device that never reaches the network behaves precisely
 * as it did before this feature existed.
 *
 * ⚠️  WHY THE LOCAL DECISION IS COMPUTED FIRST, EVEN WHEN ONLINE: it is the
 * fallback, and it is also the only thing that can refuse a forged or
 * wrong-event ticket. The server is asked to arbitrate a DUPLICATE, not to
 * verify a signature — verification is offline by design (constitution V) and
 * sending an unverified code to the server would make the gate trust the network
 * for the one thing it must never need it for.
 *
 * So: verify locally, and only if the local answer is "admit" ask the server
 * whether somebody else already did.
 */
async function decideWithServer(local, entryMethod) {
  const registrationId = local.ticket?.registrationId ?? local.registrationId;

  // Nothing to arbitrate: a local refusal is final, and an event with one device
  // has nobody to race against (FR-003).
  if (!ADMITTING.has(local.verdict) || !registrationId) return { decision: local, mode: 'offline' };
  if (!state.meta.multiDevice) return { decision: local, mode: 'offline' };
  // `navigator.onLine` false is reliable; true is not. Treated as a cheap way to
  // skip a call that would certainly fail, never as proof of connectivity — the
  // timeout is what actually decides.
  if (navigator.onLine === false) return { decision: local, mode: 'offline' };

  const scanId = crypto.randomUUID();
  try {
    const result = await checkInOnline({
      registrationId,
      scanId,
      scannedAt: new Date().toISOString(),
    });
    if (result.outcome === 'duplicate_rejected') {
      const where = result.firstCheckIn?.gateName;
      return {
        decision: {
          verdict: VERDICT.DUPLICATE,
          ticket: local.ticket,
          note: where ? `Already checked in at ${where}.` : 'Already checked in.',
        },
        // ⚠️  Already recorded server-side under `scanId`, so this must NOT be
        // queued again — the outbox would push a row that exists and the device
        // would count its own scan twice locally.
        mode: 'online',
        recorded: true,
      };
    }
    return { decision: local, mode: 'online', recorded: true };
  } catch (err) {
    /**
     * ⚠️  EVERY failure lands here, and every one of them means "decide locally".
     * Timeout, no network, 5xx, a refusal we do not understand — the volunteer
     * must never see an error, a spinner or a retry prompt (FR-005). The only
     * visible difference between online and offline is that duplicates stop
     * being caught in real time, which is a fact about the world.
     *
     * The ONE exception worth honouring: if the server explicitly said the
     * ticket is cancelled, that is newer information than this device's dataset
     * and it must win — a refunded delegate must not be admitted because the
     * device synced before the refund.
     */
    if (err?.reason === 'cancelled') {
      return {
        decision: { verdict: VERDICT.REJECT, ticket: local.ticket, note: 'This ticket was cancelled.' },
        mode: 'online',
      };
    }
    return { decision: local, mode: 'offline' };
  }
}

async function applyDecision(decision, entryMethod, { mode = 'offline', recorded = false } = {}) {
  const registrationId = decision.ticket?.registrationId ?? decision.registrationId;
  // The device clock, recorded as data — never consulted to decide anything
  // (FR-027). A gate device out of a drawer does not know what time it is.
  const scannedAt = new Date().toISOString();

  if (ADMITTING.has(decision.verdict)) {
    if (decision.ticket) await markCheckedIn(registrationId, scannedAt);
  }

  // Every verdict the outbox contract can express is recorded against a real
  // registration, including duplicates — that is what 009 reconciles into the
  // conflict feed.
  //
  // ⚠️  `recorded` means the SERVER already stored this scan during the online
  // check (feature 009). Queuing it again would push a row that exists —
  // harmless server-side thanks to ON CONFLICT, but it would inflate this
  // device's own queued count and show the volunteer work that is already done.
  const outcome = OUTCOME.get(decision.verdict);
  if (registrationId && outcome && !recorded) {
    await queueScan({ registrationId, scannedAt, outcome, entryMethod, mode });
    state.queued = await countOutbox();
  }
}

// ------------------------------------------------------------------- screens

let verdictTimer = null;

function showVerdict(decision) {
  // Replace rather than stack. Manual entry is an explicit tap, so it always
  // acts — but two verdicts on screen at once is exactly the ambiguity §8.6
  // exists to prevent, and the newer one is the one the volunteer asked for.
  document.getElementById('verdict')?.remove();
  if (verdictTimer) clearTimeout(verdictTimer);

  state.showingVerdict = true;
  const detail = decision.ticket?.ticketNumber ?? null;
  const overlay = verdictOverlay(decision.verdict, detail, decision.note);
  overlay.id = 'verdict';
  document.body.append(overlay);
  verdictTimer = setTimeout(() => {
    overlay.remove();
    state.showingVerdict = false;
    verdictTimer = null;
    // Back to scanning with no tap: a tap per delegate is a tap too many at a
    // gate (US1 scenario 6).
  }, VERDICT_MS);
}

async function handleScan(qr) {
  if (state.showingVerdict) return;
  const local = await decideFromQr(qr);
  const { decision, mode, recorded } = await decideWithServer(local, 'qr');
  await applyDecision(decision, 'qr', { mode, recorded });
  showVerdict(decision);
  refreshBar();
}

/** Returns true when the entry was acted on, so the caller knows to clear it. */
async function handleManual(digits) {
  const cleaned = digits.replace(/\D/g, '');
  if (cleaned.length !== 7) {
    // Say so. A silent no-op reads as a dead button, and the volunteer's next
    // move is to tap it harder while the queue grows.
    alertLine('Type all 7 digits of the ticket number.');
    return false;
  }
  // The stored format is `KT-1234-567`; the volunteer types only the digits.
  const ticketNumber = `KT-${cleaned.slice(0, 4)}-${cleaned.slice(4)}`;
  const ticket = await getTicketByNumber(ticketNumber);
  const decision = ticket
    ? await decideFromTicket(ticket, ticket.registrationId)
    : { verdict: VERDICT.REJECT, note: `No ticket ${ticketNumber} at this event.` };
  const settled = await decideWithServer(decision, 'manual');
  await applyDecision(settled.decision, 'manual', { mode: settled.mode, recorded: settled.recorded });
  showVerdict(settled.decision);
  refreshBar();
  return true;
}

let barNode = null;

function refreshBar() {
  const fresh = statusBar({
    gateName: state.meta.gateName,
    label: state.meta.label,
    online: navigator.onLine,
    lastSyncedAt: state.meta.lastSyncedAt,
    ticketCount: state.meta.ticketCount,
    queued: state.queued,
    onSync: onSyncNow,
  });
  if (barNode?.parentNode) barNode.replaceWith(fresh);
  barNode = fresh;
  return fresh;
}

async function onSyncNow() {
  if (!navigator.onLine) {
    alertLine('No connection — nothing to sync with. The gate still works.');
    return;
  }
  try {
    await syncNow();
    await loadMeta();
    // The queued count is what a volunteer actually watches — say it when there
    // is something left, because "synced" beside a non-zero queue reads as a lie.
    alertLine(
      state.queued > 0
        ? `Synced. ${state.meta.ticketCount} tickets, ${state.queued} scan${state.queued === 1 ? '' : 's'} still to send.`
        : `Synced. ${state.meta.ticketCount} tickets.`,
    );
  } catch (err) {
    alertLine(
      err?.reason === 'device_revoked'
        ? 'This device has been revoked by the organizer.'
        : 'Could not sync. Try again when you have signal.',
    );
  }
  refreshBar();
}

/** A transient line under the bar — never a modal, which would block a queue. */
function alertLine(text) {
  const existing = document.getElementById('flash');
  existing?.remove();
  const node = el('div', { class: 'bar', id: 'flash' }, text);
  barNode?.after(node);
  setTimeout(() => node.remove(), 3500);
}

function scanScreen() {
  const video = el('video', { playsinline: 'true' });
  // The PROPERTY, not the attribute: autoplay policy reads `video.muted`, and a
  // `muted="true"` attribute set after creation does not always move it. An
  // unmuted stream is refused playback outright, which reads as "no camera".
  video.muted = true;
  const viewport = el('div', { class: 'viewport' }, [video, el('div', { class: 'reticle' })]);

  const input = el('input', {
    class: 'manualInput',
    inputmode: 'numeric',
    pattern: '[0-9]*',
    maxlength: '9',
    placeholder: '2911 084',
    'aria-label': 'Ticket number digits',
  });
  input.addEventListener('input', () => {
    // Digits only. A volunteer typing fast must not be able to introduce
    // anything the lookup will not match (FR-023).
    const digits = input.value.replace(/\D/g, '').slice(0, 7);
    input.value = digits.length > 4 ? `${digits.slice(0, 4)} ${digits.slice(4)}` : digits;
  });

  const submit = el(
    'button',
    {
      class: 'btn btnPrimary',
      onclick: () => {
        void (async () => {
          // Cleared only once a decision was actually applied — a half-typed
          // number stays put to be finished rather than retyped from scratch.
          if (await handleManual(input.value)) input.value = '';
        })();
      },
    },
    'Check',
  );

  const manual = el('div', {}, [
    el('p', { class: 'muted' }, 'Or type the ticket number'),
    el('div', { class: 'manualRow' }, [
      // Static element, not editable text — the prefix cannot be deleted.
      el('span', { class: 'manualPrefix' }, 'KT-'),
      input,
      submit,
    ]),
  ]);

  const screen = el('div', { class: 'screen' }, [viewport, manual]);

  state.scanner = new Scanner(video, (data) => void handleScan(data));
  state.scanner.start().catch(() => {
    /**
     * The camera is unavailable or the permission was refused (FR-032).
     * Manual entry stays fully usable — the app says what happened rather than
     * presenting a dead black rectangle that looks like a crash.
     */
    viewport.replaceChildren(
      centre([
        el('h1', {}, 'No camera'),
        el(
          'p',
          { class: 'muted' },
          'Allow camera access to scan, or type the ticket number below — it works exactly the same.',
        ),
      ]),
    );
  });

  return [refreshBar(), screen];
}

/**
 * The blocking not-prepared state (FR-033, R14).
 *
 * Never a warning over a working scan screen. With an empty ticket store and no
 * public key, EVERY code — genuine or forged — looks like "valid signature, not
 * in my list", so a device in this condition would admit everybody. Refusing to
 * scan at all is the only safe response.
 */
/**
 * Is this an iPhone/iPad running in a browser tab rather than from the home
 * screen? (iOS only — Android has no equivalent hazard.)
 *
 * iPadOS presents itself as a Mac, so the touch-point count is what separates an
 * iPad from a desktop Mac; there is no distinguishing UA string. (`navigator.
 * platform` would be the usual tell and is deprecated — the UA plus touch
 * points gets the same answer without it.)
 */
function isUninstalledIOS() {
  const ios =
    /iP(hone|ad|od)/.test(navigator.userAgent) ||
    (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
  if (!ios) return false;
  const standalone =
    navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;
  return !standalone;
}

/**
 * The iOS-only instruction that has to come BEFORE provisioning.
 *
 * Safari evicts script-writable storage — IndexedDB included — after about a
 * week without interaction. Home screen web apps are exempt; a tab is not. So
 * an iPhone prepared in a tab and left in a bag until the event arrives with an
 * empty ticket store. `readiness()` catches that and refuses to scan, so it
 * fails safe rather than admitting everybody — but the device is dead at the
 * gate, which is the worst possible moment to discover it.
 *
 * `navigator.storage.persist()` in provision.js is the mitigation everywhere
 * else and is effectively a no-op on iOS, so installing first IS the mitigation
 * there. It also has to be done from Safari: Add to Home Screen from another
 * iOS browser has not reliably produced a standalone app with the same
 * storage exemption.
 */
function iosInstallNote() {
  if (!isUninstalledIOS()) return null;
  return el('div', { class: 'warnBox' }, [
    el('p', {}, 'On iPhone, install this app before setting it up.'),
    el(
      'p',
      { class: 'muted' },
      'In Safari, tap Share, then “Add to Home Screen”, and open it from the home screen icon. ' +
        'Left in a browser tab, iPhone erases the ticket list after about a week — the app will ' +
        'refuse to scan rather than admit everybody, but it will be useless at the gate.',
    ),
  ]);
}

/**
 * Provisioning by paste, for when the link cannot be opened in this copy of the
 * app. See `tokenFromText` for why iOS leaves no alternative.
 *
 * Shown on every platform rather than gated to iOS: it is also the answer when
 * the link arrives on a laptop and the gate phone has no way to receive it, and
 * a control that only some devices have is one that nobody at a gate has been
 * shown how to use.
 */
function pasteSetupLink() {
  const input = el('input', {
    class: 'setupInput',
    type: 'text',
    // A token is case-sensitive and is not a word. Every one of these is off
    // because iOS otherwise capitalises the first character and "corrects" the
    // rest, silently producing a token that will be refused as invalid.
    autocapitalize: 'off',
    autocorrect: 'off',
    autocomplete: 'off',
    spellcheck: 'false',
    placeholder: 'Paste the setup link',
    'aria-label': 'Setup link',
  });

  const status = el('p', { class: 'muted' }, '');

  const submit = el(
    'button',
    {
      class: 'btn btnPrimary btnWide',
      onclick: () => {
        const token = tokenFromText(input.value);
        if (!token) {
          status.textContent = 'That does not look like a setup link. Copy the whole link and paste it again.';
          return;
        }
        render(provisioningScreen(token));
      },
    },
    'Set up this device',
  );

  return el('div', { class: 'setupBox' }, [
    el('p', { class: 'muted' }, 'Have a setup link? Paste it here.'),
    input,
    submit,
    status,
  ]);
}

function notPreparedScreen(reason) {
  return centre([
    el('h1', {}, reason === 'not_provisioned' ? 'Not set up yet' : 'Ticket list missing'),
    el('div', { class: 'warnBox' }, [
      el(
        'p',
        {},
        reason === 'not_provisioned'
          ? 'This device has not been prepared. Ask the organizer for a setup link and open it here while you have internet.'
          : 'This device’s ticket list is gone — storage was cleared or evicted. It cannot check anyone in until it is set up again.',
      ),
      el(
        'p',
        { class: 'muted' },
        'It will not scan until then. That is deliberate: without its list it would admit everybody.',
      ),
    ]),
    // On the `data_lost` path this doubles as the explanation of what just
    // happened, not only advice for next time. `el` stringifies null children,
    // so a non-iOS device must drop it rather than pass it through.
    iosInstallNote(),
    // Both reasons need it: `data_lost` is recovered by provisioning again.
    pasteSetupLink(),
  ].filter(Boolean));
}

function provisioningScreen(token) {
  const status = el('p', { class: 'muted' }, 'Setting this device up…');
  void (async () => {
    try {
      const result = await runProvisioning(token);
      clearTokenFromLocation();
      await loadMeta();
      render(
        centre([
          el('h1', {}, 'Ready'),
          el('p', {}, `${result.gateName} — ${result.label}`),
          el('p', { class: 'muted' }, `${result.ticketCount} tickets downloaded.`),
          el('p', { class: 'muted' }, 'You can go offline now.'),
          el('button', { class: 'btn btnPrimary btnWide', onclick: () => void start() }, 'Start scanning'),
        ]),
      );
    } catch (err) {
      const message =
        {
          invalid_provisioning_token: 'This setup link has already been used, or is not valid.',
          provisioning_token_expired: 'This setup link has expired. Ask for a new one.',
          device_revoked: 'This device has been revoked by the organizer.',
          event_not_signed_yet:
            'This event has not issued any tickets yet, so there is nothing to download. Try again once the first ticket is sold.',
          offline: 'No connection. Setting up needs internet — connect and open the link again.',
        }[err?.reason] ?? 'Setup failed. Ask the organizer for a new link.';
      status.textContent = message;
    }
  })();

  return centre([el('h1', {}, 'KontriTicket check-in'), status]);
}

// -------------------------------------------------------------------- boot

async function loadMeta() {
  // ⚠️  `multiDevice` must be here (feature 009). `decideWithServer` reads it to
  // decide whether to ask the server at all, and a key absent from this list is
  // `undefined` at scan time — which reads as "I am alone" and silently disables
  // the online check on every device.
  const keys = [
    'gateName', 'label', 'eventId', 'eventName', 'publicKey', 'lastSyncedAt',
    'ticketCount', 'multiDevice',
  ];
  const values = await Promise.all(keys.map((k) => getMeta(k)));
  state.meta = Object.fromEntries(keys.map((k, i) => [k, values[i]]));
  state.queued = await countOutbox();
}

/**
 * The last resort (FR-032's sibling): a boot that threw must still say
 * something.
 *
 * A blank white screen is the one outcome a volunteer cannot act on — it is
 * indistinguishable from a dead device, and it hides the fact that manual entry
 * would have worked. IndexedDB alone can fail here for reasons the app does not
 * control: private browsing, a quota error, a corrupted store.
 */
function bootFailureScreen(err) {
  return centre([
    el('h1', {}, 'Could not start'),
    el('div', { class: 'warnBox' }, [
      el(
        'p',
        {},
        'This device could not open its ticket list. Close the app and open it again. If that does not help, ask the organizer to set the device up afresh.',
      ),
      el('p', { class: 'muted' }, String(err?.reason ?? err?.message ?? err)),
    ]),
  ]);
}

async function start() {
  try {
    state.scanner?.stop();
    state.scanner = null;
    await loadMeta();

    const token = tokenFromLocation();
    if (token) {
      render(provisioningScreen(token));
      return;
    }

    const ready = await readiness();
    if (!ready.ready) {
      render(notPreparedScreen(ready.reason));
      return;
    }

    render(...scanScreen());

    /**
     * Best-effort, and deliberately after the screen is up (feature 009,
     * FR-008).
     *
     * ⚠️  This registers a tag that the generated service worker does not yet
     * handle — see `registerBackgroundSync`. The queue is actually drained by
     * the `online` listener below and by "Sync now", both of which need the app
     * to be open. Nothing here should be read as "a closed app catches up".
     */
    void registerBackgroundSync();
  } catch (err) {
    // The console is the only diagnostic this app has — it is what a laptop on
    // chrome://inspect reads while the phone is at the gate.
    console.error('[checkin] startup failed', err);
    try {
      render(bootFailureScreen(err));
    } catch {
      // Even #app is gone. Anything on screen beats nothing.
      document.body.textContent =
        'KontriTicket check-in could not start. Close the app and open it again.';
    }
  }
}

window.addEventListener('online', refreshBar);
window.addEventListener('offline', refreshBar);

/**
 * A signal came back — drain the queue without waiting for anybody to tap
 * (feature 009).
 *
 * ⚠️  Failures are swallowed on purpose. This is opportunistic: a volunteer did
 * not ask for it, so a message about it would interrupt a queue for something
 * they did not do. The queued count in the bar is the honest signal, and
 * "Sync now" is there when they want to force it.
 */
window.addEventListener('online', () => {
  void (async () => {
    try {
      await syncNow();
      await loadMeta();
    } catch {
      // Still no usable connection. The outbox is durable; nothing is lost.
    }
    refreshBar();
  })();
});

void start();
